import React, { Suspense, useEffect, useState } from 'react'

import compareVersions from 'compare-versions'
import AppModals from 'conversations/components/App/AppModals'
import { FileUploadDropzone } from 'conversations/components/FileUploadDropzone'
import NewChatSidebar from 'conversations/components/NewChatSidebar'
import ToastNotifications from 'conversations/components/ToastNotifications'
import useAutoAwayToast from 'conversations/hooks/useAutoAwayToast'
import useBootstrap from 'conversations/hooks/useBootstrap'
import { useGlobalWebsocketHandlers } from 'conversations/hooks/useGlobalWebsocketHandlers'
import { useRefreshWhenOnline } from 'conversations/hooks/useOnline'
import useSetElectronUserId from 'conversations/hooks/useSetElectronUserId'
import useWindowFocus from 'conversations/hooks/useWindowFocus'
import { rootEpic, rootReducer } from 'conversations/modules'
import { hasLogRocket } from 'conversations/modules/gates/selectors'
import { getCurrentUser, getPreference } from 'conversations/modules/user/selectors'
import { getAppServices } from 'conversations/services'
import {
  desktopVersion,
  isRunningInBrowser,
  isRunningInDesktop,
} from 'conversations/utils/PlatformUtil'
import { lazyRetry } from 'conversations/utils/rendering/LazyRetryRender'
import { sentryConfig } from 'conversations/utils/sentry'
import { useSelector } from 'react-redux'
import { Route, Switch, useLocation } from 'react-router-dom'
import { noop } from 'rxjs'
import config from 'shared/config'
import { User } from 'shared/typings/user/User'

import { Skeleton } from '@driftt/conversation-components'
import { DriftAnalytics } from '@driftt/dds.analytics'
import { access, tracking } from '@driftt/traffic-control'

import { RootState } from 'typesafe-actions'

import '@driftt/react-dates/initialize'

import '@driftt/react-dates/lib/css/_datepicker.css'
import styles from './styles.module.css'

const ConnectedApp = lazyRetry(() => import('@driftt/traffic-control'))
const ConversationPage = lazyRetry(() => import('conversations/components/ConversationPage'))

const NewActivityWebRedirectPage = () => {
  const location = useLocation()
  /* Changes the current path to match activity-web routes */
  let newPathname = location.pathname.replace('/conversations/prospector/activity', '/activity')
  newPathname = newPathname.replace('/conversations/prospector', '/activity')
  window.location.href = `${config.BASE_URL}${newPathname}`
  return null
}

const DownloadDesktopApp = lazyRetry(() =>
  import('conversations/components/ConversationStates').then((module) => ({
    default: module.DownloadDesktopApp,
  }))
)

const OnboardingReminder = lazyRetry(() =>
  import('conversations/components/ConversationStates').then((module) => ({
    default: module.OnboardingReminder,
  }))
)

const RotatingQuote = lazyRetry(() =>
  import('conversations/components/ConversationStates').then((module) => ({
    default: module.RotatingQuote,
  }))
)

interface Props {
  setIsRbacUser: React.Dispatch<React.SetStateAction<boolean>>
}

const ConversationsApp: React.FC<Props> = ({ setIsRbacUser }) => {
  useSetElectronUserId()
  useRefreshWhenOnline()
  useAutoAwayToast()

  const currentUser = useSelector<RootState, User | undefined>(getCurrentUser)

  const onBoardingV2Dismissed = useSelector(getPreference('onBoardingV2Dismissed'))
  const orgId = currentUser?.orgId
  const currentUserId = currentUser?.id
  const hasLogRocketEnabled = useSelector(hasLogRocket)
  const [logRocketInitialized, setLogRocketInitialized] = useState(false)
  useWindowFocus()
  useBootstrap()

  useEffect(() => {
    tracking.trackPageView('Conversations Desktop')
  }, [])

  useEffect(() => {
    if (!logRocketInitialized && currentUser && hasLogRocketEnabled) {
      DriftAnalytics.initialize({
        user: currentUser,
        options: {
          logrocket: {
            networkTrackingEnabled: hasLogRocketEnabled,
            domRecordingEnabled: hasLogRocketEnabled,
          },
        },
      })
      setLogRocketInitialized(true)
    }
  }, [currentUser, hasLogRocketEnabled, logRocketInitialized])

  useEffect(() => {
    setIsRbacUser(access.isRbacUser())
  }, [currentUser, setIsRbacUser])

  const currentUserType = currentUser?.type

  useGlobalWebsocketHandlers({
    orgId,
    currentUserId,
    currentUserType,
  })

  const location = useLocation()
  const showToastNotifications = !location.pathname.includes('/prospector/')

  const conversationLandingPage = () => {
    switch (true) {
      case isRunningInBrowser && onBoardingV2Dismissed:
        return <DownloadDesktopApp />
      case !onBoardingV2Dismissed:
        return <OnboardingReminder />
      default:
        return <RotatingQuote />
    }
  }

  const isOnProspectorPage = location.pathname.includes('/prospector/')
  const hasUnifiedNav =
    currentUser?.gates?.has_unified_nav ||
    (access.isRbacUser() &&
      access.hasFeatures([{ featureName: 'prospector', accessLevel: 'READ' }]))
  const isProspectorWithUnifiedSeat = hasUnifiedNav && isOnProspectorPage

  if (!currentUser) {
    return <></>
  }

  return (
    <Route path="/conversations/:conversationId?">
      <AppModals />
      <div className={styles.app} data-ocweb-id="ConversationsMainAppWrapper">
        <div className={styles.draggableHeader} />
        <Route
          path={['/conversations/visitors/:endUserId', '/conversations/:conversationId?']}
          component={NewChatSidebar}
        />
        <Suspense fallback={isProspectorWithUnifiedSeat ? <></> : <Skeleton leftContent={<></>} />}>
          <FileUploadDropzone className={styles.rightContainer}>
            <div className={styles.routeContentContainer}>
              {showToastNotifications && <ToastNotifications />}
              <Switch>
                <Route
                  path="/conversations/prospector"
                  render={() => <NewActivityWebRedirectPage />}
                />
                <Route
                  path="/conversations/visitors/:endUserId"
                  render={(props) => <ConversationPage {...props} />}
                />
                <Route
                  path="/conversations/:conversationId"
                  render={(props) => <ConversationPage {...props} />}
                />
                <Route
                  path="/conversations/:conversationId/:messageId"
                  render={(props) => <ConversationPage {...props} />}
                />
                <Route path="/conversations">{conversationLandingPage()}</Route>
              </Switch>
            </div>
          </FileUploadDropzone>
        </Suspense>
      </div>
    </Route>
  )
}

const tcReducer = [
  {
    key: config.REDUX_STORE_PREFIX,
    reducer: rootReducer(),
  },
]

const desktopAppUpToDate =
  window.Bridge?.getVersion || (desktopVersion && compareVersions(desktopVersion, '1.5.0') >= 0)
const epics = [rootEpic]
const onLogOut = () => getAppServices().auth.logout()

// eslint-disable-next-line react/display-name
export default function () {
  const [isRbacUser, setIsRbacUser] = useState<boolean>(false)
  return (
    <Suspense fallback={<></>}>
      <ConnectedApp
        sentryConfig={sentryConfig}
        disableDrift={true}
        globalNotificationsEnabled={false}
        fasterInitialFetch={true}
        reducers={tcReducer}
        epics={epics}
        render={() => <ConversationsApp setIsRbacUser={setIsRbacUser} />}
        authClientId={'AUTH_CLIENT_ID'}
        onLogOut={onLogOut}
        onAuthError={noop}
        onIdleTimeoutRedirect={noop}
        hideAppSwitcher={isRunningInDesktop}
        hideSettings={isRbacUser}
        noNav={!desktopAppUpToDate && isRunningInDesktop}
        mountBeforeUser
      />
    </Suspense>
  )
}
